import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Play, Pause, Volume2, VolumeX, RotateCcw, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const TextToSpeech = ({ text, language = 'de', onPlay, onPause, onEnd, className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const audioRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const { user } = useAuth();
  const { toast } = useToast();

  // Voice options for different languages
  const voiceOptions = {
    de: [
      { value: 'neural-female', label: 'Neural Deutsch (Weiblich)', type: 'premium' },
      { value: 'neural-male', label: 'Neural Deutsch (Männlich)', type: 'premium' },
      { value: 'standard-female', label: 'Standard Deutsch (Weiblich)', type: 'free' },
      { value: 'standard-male', label: 'Standard Deutsch (Männlich)', type: 'free' }
    ],
    en: [
      { value: 'neural-female-us', label: 'Neural English US (Female)', type: 'premium' },
      { value: 'neural-male-us', label: 'Neural English US (Male)', type: 'premium' },
      { value: 'neural-female-uk', label: 'Neural English UK (Female)', type: 'premium' },
      { value: 'standard-female-us', label: 'Standard English US (Female)', type: 'free' }
    ],
    fr: [
      { value: 'neural-female-fr', label: 'Neural Français (Femme)', type: 'premium' },
      { value: 'neural-male-fr', label: 'Neural Français (Homme)', type: 'premium' },
      { value: 'standard-female-fr', label: 'Standard Français (Femme)', type: 'free' }
    ],
    tr: [
      { value: 'neural-female-tr', label: 'Neural Türkçe (Kadın)', type: 'premium' },
      { value: 'neural-male-tr', label: 'Neural Türkçe (Erkek)', type: 'premium' },
      { value: 'standard-female-tr', label: 'Standard Türkçe (Kadın)', type: 'free' }
    ]
  };

  const currentVoices = voiceOptions[language] || voiceOptions.de;
  const [selectedVoice, setSelectedVoice] = useState(currentVoices[0]?.value || 'neural-female');

  useEffect(() => {
    // Load user's TTS preferences
    if (user?.profile?.preferences) {
      setSpeed(user.profile.preferences.ttsSpeed || 1);
      setSelectedVoice(user.profile.preferences.ttsVoice || 'neural-female');
    }
  }, [user]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.playbackRate = speed;
    }
  }, [volume, speed]);

  const generateAudio = async (textToSpeak) => {
    setIsLoading(true);
    try {
      // Mock TTS generation - In real implementation, this would call:
      // - OpenAI TTS API
      // - ElevenLabs API
      // - Google Cloud TTS
      // - Azure Speech Services
      
      // For now, we'll use Web Speech API as fallback with enhanced settings
      if ('speechSynthesis' in window) {
        return new Promise((resolve) => {
          // Cancel any ongoing speech
          speechSynthesis.cancel();
          
          const utterance = new SpeechSynthesisUtterance(textToSpeak);
          utterance.lang = getLanguageCode(language);
          utterance.rate = speed;
          utterance.volume = volume;
          
          // Try to select the best available voice
          const voices = speechSynthesis.getVoices();
          const preferredVoice = voices.find(voice => 
            voice.lang.startsWith(getLanguageCode(language)) && 
            (voice.name.includes('Neural') || voice.name.includes('Premium'))
          ) || voices.find(voice => voice.lang.startsWith(getLanguageCode(language)));
          
          if (preferredVoice) {
            utterance.voice = preferredVoice;
          }

          utterance.onstart = () => {
            setIsPlaying(true);
            setIsPaused(false);
            onPlay && onPlay();
          };

          utterance.onend = () => {
            setIsPlaying(false);
            setIsPaused(false);
            setProgress(0);
            onEnd && onEnd();
          };

          utterance.onerror = () => {
            toast({
              title: "Audio-Fehler",
              description: "Die Sprachausgabe konnte nicht gestartet werden.",
              variant: "destructive",
            });
            setIsPlaying(false);
            setIsLoading(false);
          };

          speechSynthesis.speak(utterance);
          resolve(true);
        });
      } else {
        // Fallback: Create a mock audio URL for demonstration
        // In real implementation, this would be the API response
        const mockAudioUrl = `data:audio/wav;base64,${btoa('mock-audio-data')}`;
        setAudioUrl(mockAudioUrl);
        return true;
      }
    } catch (error) {
      console.error('TTS Error:', error);
      toast({
        title: "TTS-Fehler",
        description: "Die Sprachausgabe ist momentan nicht verfügbar.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const getLanguageCode = (lang) => {
    const codes = {
      de: 'de-DE',
      en: 'en-US',
      fr: 'fr-FR',
      tr: 'tr-TR'
    };
    return codes[lang] || 'de-DE';
  };

  const handlePlay = async () => {
    if (!text.trim()) return;

    if (isPaused && 'speechSynthesis' in window) {
      speechSynthesis.resume();
      setIsPlaying(true);
      setIsPaused(false);
      return;
    }

    const success = await generateAudio(text);
    if (!success) return;

    // Start progress tracking for Web Speech API
    if ('speechSynthesis' in window) {
      const estimatedDuration = (text.length / 10) * (1 / speed); // Rough estimation
      setDuration(estimatedDuration);
      
      progressIntervalRef.current = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 0.1;
          if (newProgress >= estimatedDuration) {
            clearInterval(progressIntervalRef.current);
            return estimatedDuration;
          }
          return newProgress;
        });
      }, 100);
    }
  };

  const handlePause = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.pause();
      setIsPlaying(false);
      setIsPaused(true);
      onPause && onPause();
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
  };

  const handleStop = () => {
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
    setIsPlaying(false);
    setIsPaused(false);
    setProgress(0);
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (progress / duration) * 100 : 0;

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Main Controls */}
      <div className="flex items-center space-x-4">
        <Button
          variant={isPlaying ? "default" : "outline"}
          size="sm"
          onClick={isPlaying ? handlePause : handlePlay}
          disabled={isLoading || !text.trim()}
          className="min-w-[100px]"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
          ) : isPlaying ? (
            <>
              <Pause className="h-4 w-4 mr-2" />
              Pause
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              {isPaused ? 'Fortsetzen' : 'Abspielen'}
            </>
          )}
        </Button>

        {(isPlaying || isPaused) && (
          <Button variant="ghost" size="sm" onClick={handleStop}>
            <RotateCcw className="h-4 w-4" />
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowSettings(!showSettings)}
        >
          <Settings className="h-4 w-4" />
        </Button>

        {/* Progress */}
        {duration > 0 && (
          <div className="flex-1 flex items-center space-x-2 text-sm text-slate-600">
            <span>{formatTime(progress)}</span>
            <div className="flex-1 bg-slate-200 rounded-full h-2">
              <div 
                className="bg-[#4ECDC4] h-2 rounded-full transition-all"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <span>{formatTime(duration)}</span>
          </div>
        )}
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-4 space-y-4">
            <h4 className="font-medium text-slate-900">Audio-Einstellungen</h4>
            
            {/* Voice Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Stimme</label>
              <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currentVoices.map((voice) => (
                    <SelectItem key={voice.value} value={voice.value}>
                      <div className="flex items-center justify-between w-full">
                        <span>{voice.label}</span>
                        {voice.type === 'premium' && (
                          <span className="text-xs bg-[#4ECDC4] text-white px-2 py-1 rounded ml-2">
                            Premium
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Speed Control */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Geschwindigkeit</label>
                <span className="text-sm text-slate-600">{speed.toFixed(1)}x</span>
              </div>
              <Slider
                value={[speed]}
                onValueChange={(value) => setSpeed(value[0])}
                max={2}
                min={0.5}
                step={0.1}
                className="w-full"
              />
            </div>

            {/* Volume Control */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Lautstärke</label>
                <div className="flex items-center space-x-2">
                  {volume === 0 ? (
                    <VolumeX className="h-4 w-4 text-slate-400" />
                  ) : (
                    <Volume2 className="h-4 w-4 text-slate-600" />
                  )}
                  <span className="text-sm text-slate-600">{Math.round(volume * 100)}%</span>
                </div>
              </div>
              <Slider
                value={[volume]}
                onValueChange={(value) => setVolume(value[0])}
                max={1}
                min={0}
                step={0.1}
                className="w-full"
              />
            </div>

            {/* Synchronization Feature Info */}
            <div className="bg-gradient-to-r from-[#4ECDC4] to-[#45b7aa] rounded-lg p-3 text-white text-sm">
              <div className="flex items-center space-x-2">
                <Volume2 className="h-4 w-4" />
                <span className="font-medium">Wort-Synchronisation verfügbar</span>
              </div>
              <p className="text-xs mt-1 opacity-90">
                Für synchronisiertes Lesen mit Wort-Highlighting verwenden Sie die Story-Ansicht.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TextToSpeech;